// Графики дизайн-системы (DESIGN §6 «Графики»): тонкие линии, мягкие заливки, подписи без мусора,
// подсказка с точными числами, одна палитра. Своё SVG вместо библиотеки: графиков немного, они простые.
// Размер — в настоящих пикселях контейнера: подписи одного размера на компьютере и телефоне, даты прорежены по
// ширине и не слипаются, на оси Y — 2–3 круглых значения. Подсказка — наведением мышью и касанием на телефоне
// (касание в другом месте её закрывает).
import {
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

export type ChartTone = 'accent' | 'success' | 'danger' | 'muted';

/** Ширина элемента в пикселях, следит за изменением размера. */
function useWidth(ref: RefObject<HTMLElement | null>): number {
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(Math.round(el.getBoundingClientRect().width));
    const ro = new ResizeObserver(([e]) => {
      if (e) setW(Math.round(e.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

/**
 * Подсказка по точке: мышь — пока над графиком; палец — по касанию (и ведением вдоль графика), закрывается
 * касанием вне графика.
 */
function usePointerIndex(
  count: number,
  box: RefObject<HTMLElement | null>,
  index: (clientX: number) => number,
) {
  const [hover, setHover] = useState<number | null>(null);
  const touched = useRef(false);
  useEffect(() => {
    if (hover === null || !touched.current) return;
    const close = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) {
        touched.current = false;
        setHover(null);
      }
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [hover, box]);
  const pick = (e: ReactPointerEvent) => {
    if (count > 0) setHover(Math.max(0, Math.min(count - 1, index(e.clientX))));
  };
  return {
    hover,
    handlers: {
      onPointerMove: (e: ReactPointerEvent) => {
        if (e.pointerType === 'mouse' || touched.current) pick(e);
      },
      onPointerLeave: (e: ReactPointerEvent) => {
        if (e.pointerType === 'mouse') setHover(null);
      },
      onPointerDown: (e: ReactPointerEvent) => {
        if (e.pointerType === 'mouse') return;
        touched.current = true;
        pick(e);
      },
    },
  };
}

/**
 * Мини-график под цифрой KPI: форма за период, без осей. По умолчанию шкала от нуля (деньги, лиды);
 * fromZero=false — от минимума с запасом, чтобы было видно движение доли в несколько пунктов (апрув 36% → 41%).
 * С labels и format — подсказка с точным числом по наведению и касанию.
 */
export function Sparkline({
  values,
  tone = 'accent',
  fromZero = true,
  labels,
  format,
}: {
  values: number[];
  tone?: ChartTone;
  fromZero?: boolean;
  /** Подпись каждой точки («24 сент.», «вчера») — для подсказки. */
  labels?: string[];
  format?: (n: number) => string;
}) {
  const id = useId();
  const box = useRef<HTMLSpanElement>(null);
  const interactive = Boolean(labels && format);
  const { hover, handlers } = usePointerIndex(interactive ? values.length : 0, box, (clientX) => {
    const r = box.current?.getBoundingClientRect();
    if (!r || r.width === 0) return 0;
    return Math.round(((clientX - r.left) / r.width) * (values.length - 1));
  });
  if (values.length < 2) return <span className="spark" aria-hidden="true" />;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const pad = Math.max((hi - lo) * 0.25, 0.5);
  const min = fromZero ? Math.min(0, lo) : lo - pad;
  const max = fromZero ? hi : hi + pad;
  const span = max - min || 1;
  const x = (i: number) => (i / (values.length - 1)) * 100;
  const y = (v: number) => 26 - ((v - min) / span) * 24;
  const line = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(2)},${y(v).toFixed(2)}`).join(' ');
  const svg = (
    <svg className={`spark spark-${tone}`} viewBox="0 0 100 28" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.18" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${line} L100,28 L0,28 Z`} fill={`url(#${id})`} stroke="none" />
      <path d={line} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      {hover !== null && (
        <line
          className="spark-cursor"
          x1={x(hover)}
          x2={x(hover)}
          y1="0"
          y2="28"
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
  if (!interactive) return svg;
  return (
    <span ref={box} className={`spark-box spark-${tone}`} {...handlers}>
      {svg}
      {hover !== null && format && (
        <span
          className="spark-tip"
          style={{ left: `${x(hover)}%` }}
          data-side={hover > (values.length - 1) / 2 ? 'left' : 'right'}
          role="status"
        >
          {labels?.[hover]}: <b>{format(values[hover] ?? 0)}</b>
        </span>
      )}
    </span>
  );
}

export interface DayPoint {
  /** YYYY-MM-DD */
  day: string;
  bar: number;
  line: number;
  dashed?: number;
}

/** Круглая шкала на 2 деления: шаг из 1 · 1.5 · 2 · 2.5 · 3 · 4 · 5 · 6 · 8 × 10ⁿ, верх = 2 шага ≥ максимума. */
export function niceScale(max: number): { step: number; top: number } {
  const m = max > 0 && Number.isFinite(max) ? max : 1;
  const raw = m / 2;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const nice = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((n) => n >= norm - 1e-9) ?? 10;
  const step = nice * mag;
  return { step, top: step * 2 };
}

/** «$1 200.00» → «$1 200»: на оси Y копейки лишние, если число круглое. */
const trimZeros = (s: string) => s.replace(/([.,])00(?=\D*$)/, '');

/**
 * График по дням: столбцы (например, расход) и линия (выплаты), пунктир — линия с прогнозом.
 * Наведение (или касание на телефоне) показывает точные числа; для диктора и клавиатуры — скрытая таблица.
 */
export function DayChart({
  points,
  labels,
  format,
  axis,
  tooltip,
  width,
  height: H = 220,
  maxBar = Number.POSITIVE_INFINITY,
  maxLabels,
  xLabel,
}: {
  points: DayPoint[];
  labels: { bar: string; line: string; dashed?: string };
  format: (n: number) => string;
  /** Подпись значения на оси Y; по умолчанию — format без «.00» у круглых чисел. */
  axis?: (n: number) => string;
  tooltip?: (p: DayPoint) => ReactNode;
  /** Ширина в пикселях; по умолчанию — ширина контейнера (график сам следит за ней). */
  width?: number;
  /** Высота в пикселях. */
  height?: number;
  /** Наибольшая ширина столбца: у короткого периода на широком экране столбцы не превращаются в плиты. */
  maxBar?: number;
  /** Не больше стольких подписей дней; по умолчанию — сколько помещается по ширине без наездов. */
  maxLabels?: number;
  /** Подпись точки, когда точка — не день, а неделя или месяц (длинный период). */
  xLabel?: (day: string) => string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const measured = useWidth(box);
  const W = Math.max(200, width ?? (measured || 700));
  const dayLabel = (d: string) =>
    xLabel?.(d) ?? new Date(`${d}T12:00:00Z`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  // «Сегодня» — только у сегодняшнего дня (у прошлого месяца последний день — просто дата)
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Tashkent' });
  const xText = (d: string) => (d === today && !xLabel ? 'сегодня' : dayLabel(d));

  const { step: tick, top } = niceScale(
    Math.max(0, ...points.flatMap((p) => [p.bar, p.line, p.dashed ?? 0])),
  );
  const axisFmt = axis ?? ((n: number) => trimZeros(format(n)));
  const ticks = [tick, top];
  const tickText = ticks.map(axisFmt);
  // Подписи оси Y — слева, в своей колонке: её ширина — по самой длинной подписи
  const gutter = Math.round(
    Math.min(90, Math.max(28, Math.max(...tickText.map((t) => t.length)) * 6.6 + 10)),
  );
  const pad = { l: gutter, r: 8, t: 12, b: 26 };
  const plotW = W - pad.l - pad.r;
  const step = plotW / Math.max(1, points.length);
  const cx = (i: number) => pad.l + step * i + step / 2;
  const bar = Math.min(step * 0.6, maxBar);
  const y = (v: number) => pad.t + (H - pad.t - pad.b) * (1 - v / top);
  const path = (key: 'line' | 'dashed') =>
    points.map((p, i) => `${i ? 'L' : 'M'}${cx(i).toFixed(1)},${y(p[key] ?? 0).toFixed(1)}`).join(' ');

  // Даты — сколько помещается без наездов: ширина самой длинной подписи плюс воздух
  const longest = Math.max(1, ...points.map((p) => xText(p.day).length));
  const fit = Math.max(1, Math.floor(plotW / (longest * 6.6 + 16)));
  const every = Math.max(
    1,
    Math.ceil(points.length / fit),
    maxLabels ? Math.ceil(points.length / maxLabels) : 1,
  );

  const { hover, handlers } = usePointerIndex(points.length, box, (clientX) => {
    const r = box.current?.getBoundingClientRect();
    if (!r || r.width === 0) return 0;
    const px = ((clientX - r.left) / r.width) * W;
    return Math.floor((px - pad.l) / step);
  });
  const h = hover === null ? null : points[hover];

  return (
    <div className="daychart" ref={box}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${labels.bar} и ${labels.line} по дням`}
        {...handlers}
      >
        <line className="dc-base" x1={pad.l} x2={W - pad.r} y1={y(0)} y2={y(0)} />
        {ticks.map((g, i) => (
          <g key={g}>
            <line className="dc-grid" x1={pad.l} x2={W - pad.r} y1={y(g)} y2={y(g)} />
            <text className="dc-y" x={pad.l - 6} y={y(g) + 4} textAnchor="end">
              {tickText[i]}
            </text>
          </g>
        ))}
        {points.map((p, i) => (
          <rect
            key={p.day}
            className={`dc-bar${hover === i ? ' on' : ''}`}
            x={cx(i) - bar / 2}
            width={bar}
            y={y(p.bar)}
            height={Math.max(0, H - pad.b - y(p.bar))}
            rx="3"
          />
        ))}
        {points.some((p) => p.dashed !== undefined) && <path className="dc-dashed" d={path('dashed')} />}
        <path className="dc-line" d={path('line')} />
        {hover !== null && (
          <line className="dc-cursor" x1={cx(hover)} x2={cx(hover)} y1={pad.t} y2={H - pad.b} />
        )}
        {points.map((p, i) => {
          if ((points.length - 1 - i) % every !== 0) return null;
          // У краёв подпись не вылезает за график: прижата к краю, а не по центру столбца
          const text = xText(p.day);
          const half = (text.length * 6.6) / 2;
          const [x, anchor]: [number, 'start' | 'middle' | 'end'] =
            cx(i) + half > W ? [W - 1, 'end'] : cx(i) - half < 0 ? [1, 'start'] : [cx(i), 'middle'];
          return (
            <text key={p.day} className="dc-x" x={x} y={H - 8} textAnchor={anchor}>
              {text}
            </text>
          );
        })}
      </svg>
      {h && hover !== null && (
        <div
          className="dc-tip"
          style={{ left: `${(cx(hover) / W) * 100}%` }}
          data-side={hover > points.length / 2 ? 'left' : 'right'}
        >
          <b>{dayLabel(h.day)}</b>
          {tooltip ? (
            tooltip(h)
          ) : (
            <>
              <span>
                {labels.bar}: {format(h.bar)}
              </span>
              <span>
                {labels.line}: {format(h.line)}
              </span>
            </>
          )}
        </div>
      )}
      <div className="dc-legend" aria-hidden="true">
        <span className="lg-bar">{labels.bar}</span>
        <span className="lg-line">{labels.line}</span>
        {labels.dashed && <span className="lg-dashed">{labels.dashed}</span>}
      </div>
      <table className="sr-only">
        <caption>
          {labels.bar} и {labels.line} по дням
        </caption>
        <thead>
          <tr>
            <th>День</th>
            <th>{labels.bar}</th>
            <th>{labels.line}</th>
            {labels.dashed && <th>{labels.dashed}</th>}
          </tr>
        </thead>
        <tbody>
          {points.map((p) => (
            <tr key={p.day}>
              <td>{dayLabel(p.day)}</td>
              <td>{format(p.bar)}</td>
              <td>{format(p.line)}</td>
              {labels.dashed && <td>{format(p.dashed ?? 0)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
